/**
 * AdoraCare - Case Sheet V1 label dictionary (SANDBOX-CASE-SHEET-WORKBENCH-V1).
 *
 * The FIRST consumer of the "Label Dictionary Rule" (ADR-0019 / SANDBOX-CASE-
 * SHEET-SCHEMA-V1 Part C): the doctor cockpit renders user-facing clinic terms
 * from THIS module and never hardcodes a label string inline. A friendly rename
 * here changes NO database column, backend field, or controlled-vocabulary
 * token - it is a pure display mapping. Keyed by stable keys (the backend
 * field / table / status names), so a rename is a one-line edit here.
 *
 * WHY a plain TS module (no i18n framework). The repo has no i18n layer today
 * (labels are inline strings across the dashboard); a single typed dictionary is
 * the smallest faithful step and is trivially unit-testable. When a real i18n
 * layer arrives, this dictionary is the seed.
 *
 * SCOPE. Display labels ONLY. The controlled decline-reason tokens keep reusing
 * the existing OUTCOME_REASON_OPTIONS / outcomeReasonLabel from Lead360Panel (no
 * second source of truth for that value domain).
 */

/**
 * Table / layer / field -> friendly clinic term. The three clinical layers
 * (ADR-0019) map to plain-English clinic words the doctor uses:
 *   case_sheets           -> "Visit Notes"      (the VISIT)
 *   treatment_courses     -> "Treatment Journey" (the long multi-visit course)
 *   treatment_plan_items  -> "Procedures"        (the line items)
 * The DB names are NOT renamed - only these display strings.
 */
export const CASE_SHEET_LABELS = {
  // Layer / table labels
  caseSheet: 'Visit Notes',
  treatmentCourse: 'Treatment Journey',
  treatmentPlan: 'Treatment Plan',
  treatmentPlanItems: 'Procedures',
  // Field labels
  chiefComplaint: 'Why patient came',
  findingsDiagnosis: 'Doctor Findings',
  clinicalNote: 'Doctor Note',
  procedureName: 'Procedure',
  // DOCTOR-CONSENT-REMARKS-V1: the doctor's per-line remark. DISTINCT from
  // clinicalNote above (the VISIT-grain doctor note) and from a consent
  // document's own notes -- three different fields, three different homes.
  itemNote: 'Remark',
  toothArea: 'Tooth / Area',
  // Structured tooth references (TOOTH-NOTATION-INTERIM-V1): the odontogram
  // selection at both grains; the free-text toothArea above stays untouched.
  toothRefs: 'Teeth',
  estimatedValue: 'Estimated Cost',
  priorityRank: 'Priority',
  declineReason: 'Decline Reason',
  visitOutcome: 'Consultation Outcome',
  // Encounter-section fields (20260728_1; DOCTOR-CLINICAL-ENCOUNTER-
  // FOUNDATION-V1). medicalDentalHistory / examinationFindings are the two
  // new storage columns; the follow-up pair is the doctor's recommendation.
  medicalDentalHistory: 'Medical & Dental History',
  examinationFindings: 'Examination Findings',
  followUp: 'Follow-up',
  followUpOption: 'Follow-up recommendation',
  followUpNote: 'Instructions for the front desk',
} as const;

/**
 * Config-backed NOTE-SECTION display labels, keyed by the CLOSED backend
 * section-key vocabulary (lib/domain/clinical/case-sheet.ts
 * CASE_SHEET_NOTE_SECTION_KEYS). The seeded Partha default section names
 * (founder ruling 2026-07-27); a friendly rename here changes NO column,
 * token, or config value. NOTE the storage honesty: 'diagnosis' renders the
 * historical combined findings_diagnosis column (existing content preserved);
 * 'examination_findings' is the NEW dedicated column.
 */
export const NOTE_SECTION_LABELS: Readonly<Record<string, string>> = {
  chief_complaint: 'Chief Complaint',
  medical_dental_history: 'Medical & Dental History',
  examination_findings: 'Examination Findings',
  diagnosis: 'Diagnosis',
  clinical_notes: 'Clinical Notes',
};

/** Friendly label for a note-section key, or the raw token as a fallback
 *  (an unknown config token must never crash a render -- fail-safe). */
export function noteSectionLabel(key: string): string {
  return NOTE_SECTION_LABELS[key] ?? key;
}

export type CaseSheetLabelKey = keyof typeof CASE_SHEET_LABELS;

/** Look up a field / layer label by its stable key. Total (the key type is
 *  closed), so this never falls back - but kept as a function so the cockpit
 *  reads labels through ONE call site, not scattered object access. */
export function caseSheetLabel(key: CaseSheetLabelKey): string {
  return CASE_SHEET_LABELS[key];
}

// --- Status token -> friendly label -----------------------------------------

/** case_sheets.status friendly labels. */
export const CASE_SHEET_STATUS_LABELS: Readonly<Record<string, string>> = {
  draft: 'Draft',
  finalized: 'Finalized',
};

/** treatment_plan_items.status friendly labels. 'Deferred' = the patient
 *  explicitly postponed the item (20260714_2) -- a recorded decision,
 *  distinct from the undecided Pending/Advised states. */
export const PLAN_ITEM_STATUS_LABELS: Readonly<Record<string, string>> = {
  advised: 'Advised',
  accepted: 'Accepted',
  declined: 'Declined',
  pending: 'Pending',
  deferred: 'Deferred',
};

/** treatment_courses.status friendly labels. */
export const TREATMENT_COURSE_STATUS_LABELS: Readonly<Record<string, string>> = {
  planned: 'Planned',
  active: 'Active',
  completed: 'Completed',
  abandoned: 'Abandoned',
};

/** Friendly label for a case-sheet status, or the raw token as a safe fallback. */
export function caseSheetStatusLabel(status: string): string {
  return CASE_SHEET_STATUS_LABELS[status] ?? status;
}

/** Friendly label for a plan-item status, or the raw token as a safe fallback. */
export function planItemStatusLabel(status: string): string {
  return PLAN_ITEM_STATUS_LABELS[status] ?? status;
}

/** Friendly label for a treatment-course status, or the raw token as a fallback. */
export function treatmentCourseStatusLabel(status: string): string {
  return TREATMENT_COURSE_STATUS_LABELS[status] ?? status;
}
